import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  IonApp, IonContent, IonBadge, IonButton, IonIcon, IonInput, IonSpinner, IonCard,
  IonCheckbox,
} from "@ionic/react";
import { personOutline, closeOutline, checkmarkOutline, downloadOutline, chatbubbleOutline, sunnyOutline, moonOutline, chevronBackOutline, refreshOutline, timeOutline } from "ionicons/icons";
import RippleButton from "@/components/RippleButton";
import LanguagePicker from "@/components/LanguagePicker";
import { setLanguage, useLanguage } from "@/utils/i18n";
import { startLiveTranslation } from "@/utils/liveTranslate";
import CommentsThreadModal from "@/components/CommentsThreadModal";
import { fileToPreviewPages } from "@/components/DocumentPreviewModal";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { saveBlob } from "@/utils/archivedFile";
import { isNative } from "@/utils/nativeHttp";
import SignaturePad from "@/components/SignaturePad";
import SignedCheck from "@/components/SignedCheck";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import PreviewPager from "@/components/PreviewPager";
import SignDocCanvas from "@/components/SignDocCanvas";
import EditSheet from "@/components/EditSheet";
import { pdfToPageImages } from "@/utils/pdfPageImages";
import { stampSignDoc, missingRequired, todayLong, b64ToBytes, fieldMeta } from "@/utils/signDocFields";
import { brandVars, brandWavesVars, DEFAULT_BRAND_COLOR } from "@/utils/brandColor";
import MintSlipLogo from "@/assests/mintslip-logo.png";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { promptAlert, confirmAlert } from "@/utils/confirmAlert";
import { toast } from "@/utils/toast";
import { toastText } from "@/utils/toastTemplates";
import { LEGAL_FORM_CONFIGS } from "@/pages/app/appLegalFormConfigs";
import { BUSINESS_FORM_CONFIGS } from "@/pages/app/appBusinessFormConfigs";
import { generateOfferLetterPreviewPages } from "@/utils/offerLetterPreviewGenerator";
import { generateAndDownloadOfferLetter } from "@/utils/offerLetterGenerator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SIGN_THEME_KEY = "signLightMode"; // a guest signer's own light/dark choice on this device

// The public signing page (/sign/<token>): the other party opens the link
// from their email, sees the document over the sender's brand colours,
// draws their signature and approves — or declines with a reason. The
// final signed PDF is generated right here (only a browser can run the
// generators) and archived back to the sender's business.

const CONFIGS = { ...LEGAL_FORM_CONFIGS, ...BUSINESS_FORM_CONFIGS };

const engineFor = (documentType) => {
  if (documentType === "offer-letter") {
    return {
      preview: (fd) => generateOfferLetterPreviewPages(fd),
      download: (fd) => generateAndDownloadOfferLetter(fd, true),
      merge: (fd, sig, name) => ({
        ...fd, employeeSignatureImage: sig, employeeSignatureType: "custom",
        employeeSignatureName: name || fd.employeeSignatureName || "",
        employeeSignDate: fd.employeeSignDate || new Date().toISOString().slice(0, 10),
      }),
    };
  }
  const cfg = Object.values(CONFIGS).find((c) => c.docType === documentType);
  if (!cfg) return null;
  return {
    preview: (fd, taxYear) => cfg.preview(fd, taxYear),
    download: (fd, taxYear) => cfg.download(fd, taxYear, true),
    merge: (fd, sig) => fd, // fieldName merge happens at the call site
  };
};

// The audit page appended to the signed PDF: who, when, from where.
async function appendCertificate(blob, cert, req, signerName, signature) {
  try {
    const pdf = await PDFDocument.load(await blob.arrayBuffer());
    const page = pdf.addPage([612, 792]);
    const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    let y = 720;
    page.drawText("Signature Certificate", { x: 50, y, size: 22, font: bold, color: rgb(0.1, 0.12, 0.15) });
    y -= 36;
    // values wrap to the page margin instead of running off the edge (the
    // Device line is a full user-agent string)
    const valueX = 185, maxW = 612 - valueX - 50;
    const wrap = (text) => {
      const rows = [];
      let cur = "";
      for (let word of String(text).split(" ")) {
        while (font.widthOfTextAtSize(word, 10) > maxW) {
          let i = word.length;
          while (i > 1 && font.widthOfTextAtSize(word.slice(0, i), 10) > maxW) i--;
          rows.push(cur ? `${cur} ${word.slice(0, i)}` : word.slice(0, i));
          cur = ""; word = word.slice(i);
        }
        const test = cur ? `${cur} ${word}` : word;
        if (cur && font.widthOfTextAtSize(test, 10) > maxW) { rows.push(cur); cur = word; }
        else cur = test;
      }
      if (cur) rows.push(cur);
      return rows.length ? rows : ["\u2014"];
    };
    const line = (k, v) => {
      page.drawText(k, { x: 50, y, size: 10, font: bold });
      const rows = wrap(String(v || "\u2014").slice(0, 300));
      rows.forEach((row, i) => {
        page.drawText(row, { x: valueX, y, size: 10, font });
        y -= i === rows.length - 1 ? 18 : 14;
      });
    };
    line("Sent by", req.branding?.name);
    const chain = Array.isArray(cert.signers) ? cert.signers : [];
    if (chain.length > 1) {
      // several signers: each one's record, in signing order
      chain.forEach((sg, i) => {
        y -= 4;
        page.drawText(`Signer ${i + 1}`, { x: 50, y, size: 11, font: bold, color: rgb(0.1, 0.12, 0.15) });
        y -= 16;
        line("Signed by", sg.name ? `${sg.name} (${sg.email})` : sg.email);
        line("Signed at (UTC)", sg.signedAt);
        line("IP address", sg.ip);
        line("Device", sg.userAgent);
      });
      y -= 4;
      line("Request ID", cert.requestId);
    } else {
      line("Sent to", req.recipientEmail);
      line("Signed by", signerName || req.recipientName || req.recipientEmail);
      line("Signed at (UTC)", cert.signedAt);
      line("IP address", cert.ip);
      line("Device", cert.userAgent);
      line("Request ID", cert.requestId);
    }
    y -= 10;
    if (chain.length > 1 || !signature) {
      // the signatures themselves are on the document
      page.drawText("Recorded by MintSlip at the moment of signing.", { x: 50, y: 60, size: 9, font, color: rgb(0.42, 0.46, 0.5) });
      return new Blob([await pdf.save()], { type: "application/pdf" });
    }
    page.drawText("Signature:", { x: 50, y, size: 10, font: bold });
    y -= 120;
    try {
      const png = await pdf.embedPng(signature);
      const dims = png.scale(Math.min(1, 260 / png.width, 110 / png.height));
      page.drawImage(png, { x: 50, y, width: dims.width, height: dims.height });
    } catch { /* the drawn image failed to embed; the record above stands */ }
    page.drawText("Recorded by MintSlip at the moment of signing.", { x: 50, y: 60, size: 9, font, color: rgb(0.42, 0.46, 0.5) });
    return new Blob([await pdf.save()], { type: "application/pdf" });
  } catch (e) {
    console.error("Certificate append failed:", e);
    return blob;
  }
}

const blobToB64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(String(r.result).split(",")[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

// A phone number reads like one: (512) 555-0100, or +1 in front of eleven digits.
const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p;
};

const STATUS_BADGE = {
  pending: ["warning", "Pending"],
  signed: ["success", "Signed"],
  declined: ["danger", "Declined"],
  expired: ["medium", "Expired"],
  canceled: ["medium", "Canceled"],
};

// A final signature from anyone the sender has not marked trusted waits for
// the sender's approval; only then is the clean signed copy made.
const awaitingApproval = (r) => r?.status === "signed" && r?.approval === "pending";

export default function SignRequest() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The owner's way back (the login page's chevron): a real in-app page
  // behind us means go back; opened cold, the signatures page is home.
  const goBack = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate("/app/signatures", { replace: true });
  };
  const [req, setReq] = useState(null);
  const [error, setError] = useState("");
  const [shell, setShell] = useState(null);     // the masked view, pre-verification
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [pages, setPages] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [finalBlob, setFinalBlob] = useState(null);
  // The page's theme survives a refresh. A signed-in signer's sun/moon is
  // their app theme (the same key the app's own toggles write), so a choice
  // made here follows them into the app and back; a guest's choice is kept
  // for them on this device. With no choice made yet the page is dark.
  const [lightTheme, setLightTheme] = useState(() => {
    try {
      if (isUserLoggedIn()) {
        const stored = localStorage.getItem("appDarkMode");
        return stored === "false" ? true : false;
      }
      return localStorage.getItem(SIGN_THEME_KEY) === "true";
    } catch { return false; }
  });
  // The signature landed in this session: the success mark animates in.
  // A page opened already signed shows the finished mark.
  const [justSigned, setJustSigned] = useState(false);
  // the signer picks the page's language beside the theme toggle; the live
  // translator (the app's) swaps the page's text, the document stays as made
  const language = useLanguage();
  useEffect(() => startLiveTranslation(), []);

  const toggleTheme = () => setLightTheme((v) => {
    const next = !v;
    try {
      if (isUserLoggedIn()) localStorage.setItem("appDarkMode", String(!next));
      else localStorage.setItem(SIGN_THEME_KEY, String(next));
    } catch { /* the page still flips */ }
    return next;
  });
  // An uploaded document (documentType "custom-pdf"): its PDF, drawn pages,
  // this signer's values, and the field whose signature is being drawn
  const isCustom = req?.documentType === "custom-pdf";
  const [docBytes, setDocBytes] = useState(null);
  const [docPages, setDocPages] = useState(null);
  const [myValues, setMyValues] = useState({});
  const [padFor, setPadFor] = useState(null);
  const [padSig, setPadSig] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "This signing link could not be opened");
      if (d.request.requiresVerification === false) {
        // the sender's own link: no email gate
        setReq(d.request);
        setComments(d.request.comments || []);
      } else {
        setShell(d.request);
      }
    } catch (err) { setError(err.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // an uploaded document: its PDF, for the pages and the final stamp
  useEffect(() => {
    if (!req || req.documentType !== "custom-pdf" || docBytes) return undefined;
    let on = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/file`, {
          method: "POST", headers: req.owner ? userHeaders({ "Content-Type": "application/json" }) : { "Content-Type": "application/json" },
          body: JSON.stringify({ email: verifyEmail.trim() }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || !d.fileData) throw new Error(d.detail || "The document could not be loaded");
        const bytes = b64ToBytes(d.fileData);
        if (!on) return;
        setDocBytes(bytes);
        const imgs = await pdfToPageImages(bytes.slice(0), { scale: 1.5, maxPages: 40 });
        if (on) setDocPages(imgs);
      } catch (err) { if (on) toast.error(err.message); }
    })();
    return () => { on = false; };
  }, [req?.id, req?.documentType, req?.owner]); // eslint-disable-line

  // the identity check: only the address it was sent to opens the document
  const verify = async () => {
    if (!verifyEmail.includes("@")) { toast.error("Enter the email this was sent to"); return; }
    setVerifying(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not verify");
      setReq(d.request);
      setComments(d.request.comments || []);
      setSignerName((prev) => prev || d.request.recipientName || "");
    } catch (err) { toast.error(err.message); } finally { setVerifying(false); }
  };

  // the document, previewed with the sender's data — and re-previewed live
  // with the signature as it is drawn
  useEffect(() => {
    if (!req) return;
    // a signed document shows its actual signed copy (certificate included),
    // loaded by the effect below — not a regenerated preview
    if (req.status === "signed" && (finalBlob || (req.owner && req.docId))) return;
    let on = true;
    const t = setTimeout(async () => {
      try {
        const engine = engineFor(req.documentType);
        if (!engine) return;
        // the signature being drawn, or the stored one on a signed request
        const sig = signature || (req.status === "signed" ? req.signatureData : null);
        const name = signerName || req.signerName || "";
        const fd = sig
          ? (req.documentType === "offer-letter"
              ? engine.merge(req.formData || {}, sig, name)
              : { ...(req.formData || {}), [req.fieldName]: sig })
          : (req.formData || {});
        const img = await engine.preview(fd, req.taxYear);
        if (on) setPages(Array.isArray(img) ? img : img ? [img] : []);
      } catch (e) { console.error("Preview failed:", e); }
    }, signature ? 350 : 0);
    return () => { on = false; clearTimeout(t); };
  }, [req, signature, signerName, finalBlob]);

  // Signed: the archived signed copy drives the page. The sender fetches it
  // from their documents; a signer who just finished already holds it. It
  // renders as the preview and is what Download PDF saves.
  useEffect(() => {
    if (!req || req.status !== "signed") return;
    let on = true;
    (async () => {
      try {
        let blob = finalBlob;
        if (!blob && req.owner && req.docId) {
          const res = await fetch(`${BACKEND_URL}/api/my-documents/${encodeURIComponent(req.docId)}/download`, { headers: userHeaders() });
          if (!res.ok) return;
          blob = await res.blob();
          if (on) setFinalBlob(blob);
        }
        if (!blob && !req.owner && req.docId && !awaitingApproval(req)) {
          // the signer's copy, released once the sender approved it
          const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/signed-copy`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: verifyEmail.trim() }),
          });
          const d = await res.json().catch(() => ({}));
          if (!res.ok || !d.fileData) return;
          const bytes = Uint8Array.from(atob(d.fileData), (c) => c.charCodeAt(0));
          blob = new Blob([bytes], { type: "application/pdf" });
          if (on) setFinalBlob(blob);
        }
        if (!blob) return;
        const imgs = await fileToPreviewPages(blob, "signed.pdf");
        if (on && imgs.length) setPages(imgs);
      } catch (e) { console.error("Signed copy load failed:", e); }
    })();
    return () => { on = false; };
  }, [req, finalBlob]); // eslint-disable-line

  // The final signed copy: the document with the signature merged in and the
  // signature certificate appended. Built by a trusted signer's browser at
  // signing, or by the sender's when they approve.
  const buildFinal = async (sig, name, certificate) => {
    const engine = engineFor(req.documentType);
    const merged = req.documentType === "offer-letter"
      ? engine.merge(req.formData || {}, sig, name)
      : { ...(req.formData || {}), [req.fieldName]: sig };
    let blob = await engine.download(merged, req.taxYear);
    if (blob instanceof Blob && certificate) blob = await appendCertificate(blob, certificate, req, name, sig);
    return blob instanceof Blob ? blob : null;
  };
  const buildCustomFinal = async (values, name, certificate) => {
    if (!docBytes) throw new Error("The document is still loading");
    let blob = await stampSignDoc(docBytes.slice(0), req.docFields || [], values || {});
    const firstSig = (req.docFields || []).map((f) => f.type === "signature" && values?.[f.id]).find(Boolean) || null;
    if (certificate) blob = await appendCertificate(blob, certificate, req, name, firstSig);
    return blob;
  };
  // this signer's values as they stand, with their date fields set to today
  const myFieldValues = () => {
    const out = { ...myValues };
    for (const f of req?.docFields || []) if (f.role === req.roleId && f.type === "date") out[f.id] = todayLong(f.format);
    return out;
  };
  const finalFileName = () => `${(req.branding?.name || "document").replace(/\s+/g, "-")}-${req.documentType}-signed.pdf`;
  // of the fields still empty, the one to go to: on the page shown, else the earliest page
  const nextMissing = (missing) => missing.find((f) => f.page === pageIdx) || [...missing].sort((a, b) => (a.page || 0) - (b.page || 0))[0];

  const signCustom = async () => {
    const values = myFieldValues();
    const missing = missingRequired(req.docFields, req.roleId, values);
    if (missing.length) {
      toast.error(`Fill in ${missing.length === 1 ? (missing[0].label || fieldMeta(missing[0].type).label.toLowerCase()) : `the ${missing.length} highlighted fields`} first`);
      const next = nextMissing(missing);
      if (typeof next?.page === "number") setPageIdx(next.page); // turn to where it waits
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail.trim(), signerName, fieldValues: values }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not sign");
      const all = { ...(req.fieldValues || {}), ...values };
      setJustSigned(true);
      if (d.final === false) {
        setReq((r) => ({ ...r, status: "signed", signerName, fieldValues: all, isFinal: false }));
        toast.success(toastText("signed_forwarded_toast", {}, "Signed and sent on for the next signature"));
        return;
      }
      if (d.approval === "pending") {
        setReq((r) => ({ ...r, status: "signed", approval: "pending", signerName, fieldValues: all, isFinal: true }));
        toast.success("Signed, waiting for approval");
        return;
      }
      // a trusted signer: the stamped copy is made here and archived
      try {
        const blob = await buildCustomFinal(all, signerName, d.certificate);
        setFinalBlob(blob);
        await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/archive`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fileName: finalFileName(), fileData: await blobToB64(blob) }),
        });
      } catch (e) { console.error("Final copy failed:", e); }
      setReq((r) => ({ ...r, status: "signed", approval: "approved", signerName, fieldValues: all, isFinal: true }));
      toast.success(toastText("signed_thanks", {}, "Signed. Thank you!"));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const sign = async () => {
    if (isCustom) return signCustom();
    if (!signature) { toast.error("Draw your signature first"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail.trim(), signatureData: signature, signerName }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not sign");
      if (d.final === false) {
        // ordered signing: the next party's copy archives, not this one
        setJustSigned(true);
        setReq((r) => ({ ...r, status: "signed", signerName, isFinal: false }));
        toast.success(toastText("signed_forwarded_toast", {}, "Signed and sent on for the next signature"));
        return;
      }
      if (d.approval === "pending") {
        // the sender approves it before any clean copy exists
        setJustSigned(true);
        setReq((r) => ({ ...r, status: "signed", approval: "pending", signerName, signatureData: signature, isFinal: true }));
        toast.success("Signed, waiting for approval");
        return;
      }
      // a trusted signer: the final signed PDF is made here, with its
      // signature certificate page, and handed back for the archive
      let blob = null;
      try { blob = await buildFinal(signature, signerName, d.certificate); }
      catch (e) { console.error("Final generation failed:", e); }
      if (blob) {
        setFinalBlob(blob);
        try {
          await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/archive`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fileName: finalFileName(), fileData: await blobToB64(blob) }),
          });
        } catch (e) { console.error("Archive failed:", e); }
      }
      setJustSigned(true);
      setReq((r) => ({ ...r, status: "signed", approval: "approved", signerName, isFinal: true }));
      toast.success(toastText("signed_thanks", {}, "Signed. Thank you!"));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const decline = async () => {
    // the page's own light/dark choice, not the app's, and a roomier box
    const cssClass = `sign-alert ${lightTheme ? "sign-alert-light" : "sign-alert-dark"}`;
    const reason = await promptAlert({
      header: "Reason for declining", message: "Let the sender know why (optional).",
      placeholder: "Reason", confirmText: "Decline", allowEmpty: true, cssClass,
    });
    if (reason === null) return;
    if (!(await confirmAlert({ header: "Decline this document?", message: "The sender is told you declined.", confirmText: "Decline", cssClass }))) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/decline`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail.trim(), reason }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not decline");
      setJustSigned(true); // the decline's X animates in, as a signature's check does
      setReq((r) => ({ ...r, status: "declined" }));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  // the sender's review of a signature waiting for approval
  const [trustSigner, setTrustSigner] = useState(false);
  const alertClass = () => `sign-alert ${lightTheme ? "sign-alert-light" : "sign-alert-dark"}`;
  const approve = async () => {
    if (!(await confirmAlert({
      header: "Approve this signature?",
      message: "The final signed copy is made and saved to your documents, and the signer can download it.",
      confirmText: "Approve", cssClass: alertClass(),
    }))) return;
    setBusy(true);
    try {
      const blob = isCustom
        ? await buildCustomFinal(req.fieldValues || {}, req.signerName || req.recipientName || "", req.certificate)
        : await buildFinal(req.signatureData, req.signerName || req.recipientName || "", req.certificate);
      if (!blob) throw new Error("Could not prepare the signed copy");
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests/${encodeURIComponent(req.id)}/approve`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ fileName: finalFileName(), fileData: await blobToB64(blob), trust: trustSigner }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not approve");
      setFinalBlob(blob);
      setJustSigned(true);
      setReq((r) => ({ ...r, approval: "approved", approvedAt: d.approvedAt, docId: d.documentId, trusted: r.trusted || trustSigner }));
      toast.success("Signature approved");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const sendBack = async () => {
    const reason = await promptAlert({
      header: "Send back to re-sign", message: `Tell ${req.recipientName || req.recipientEmail} what to fix (optional).`,
      placeholder: "Reason", confirmText: "Send back", allowEmpty: true, cssClass: alertClass(),
    });
    if (reason === null) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests/${encodeURIComponent(req.id)}/reject`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ reason }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not send it back");
      setReq((r) => ({ ...r, status: "pending", approval: null, signatureData: null, signerName: null, signedAt: null, rejectReason: reason, rejectCount: d.rejectCount }));
      setPages([]);
      toast.success(`Sent back to ${req.recipientName || req.recipientEmail}`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const downloadCopy = () => {
    if (!finalBlob) return;
    saveBlob(finalBlob, `${label} (signed).pdf`);
  };

  // a signed document downloads only as its final, approved copy
  const downloadLocked = !!req && ((req.status === "signed" && !finalBlob) || ["expired", "canceled"].includes(req.status));
  const downloadPdf = async () => {
    if (finalBlob) return downloadCopy();
    if (downloadLocked) return;
    try {
      const engine = engineFor(req.documentType);
      const blob = isCustom
        ? (docBytes ? new Blob([docBytes], { type: "application/pdf" }) : null)
        : await engine.download({ ...(req.formData || {}) }, req.taxYear);
      if (!(blob instanceof Blob)) throw new Error("empty");
      if (isNative) { saveBlob(blob, `${label}.pdf`); return; }
      // browsers: a new tab so the signing page stays put
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch { toast.error("Could not prepare the PDF"); }
  };

  // open the thread, refreshing it quietly so the sender's replies show
  const openComments = async () => {
    setCommentsOpen(true);
    try {
      const res = req?.owner
        ? await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}`, { headers: userHeaders() })
        : await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/verify`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: verifyEmail.trim() }),
          });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.request) setComments(d.request.comments || []);
    } catch { /* the local copy stands */ }
  };

  // Opened from a new-comment notification (bell or push): ?comments=1
  // opens the thread as soon as the request has loaded, once.
  useEffect(() => {
    if (!req || new URLSearchParams(location.search).get("comments") !== "1") return;
    navigate(location.pathname, { replace: true }); // the thread opens once, not on every refresh
    if (req.owner || verifyEmail.trim()) openComments();
  }, [req?.id, req?.status]); // eslint-disable-line

  const addComment = async (images = []) => {
    const text = commentText.trim();
    if (!text && images.length === 0) { toast.error("Write a comment first"); return false; }
    setCommentBusy(true);
    try {
      // the sender's comments go through their own authenticated endpoint
      const res = req?.owner && req?.id
        ? await fetch(`${BACKEND_URL}/api/user/signature-requests/${req.id}/comments`, {
            method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({ text, images }),
          })
        : await fetch(`${BACKEND_URL}/api/sign/${encodeURIComponent(token)}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail.trim(), text, images, name: signerName.trim() || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not add the comment");
      setComments(d.comments || []);
      setCommentText("");
      return true;
    } catch (err) { toast.error(err.message); return false; } finally { setCommentBusy(false); }
  };

  const color = (req || shell)?.branding?.color || DEFAULT_BRAND_COLOR;
  const vars = brandVars(color);
  Object.assign(vars, brandWavesVars(color));
  const [badgeColor, badgeText] = awaitingApproval(req) ? ["warning", "Awaiting approval"] : (STATUS_BADGE[req?.status] || ["medium", "…"]);
  const label = req?.documentType === "custom-pdf" ? (req.signDocName || "Document") : { "offer-letter": "Job Offer Letter", "power-of-attorney": "Power of Attorney", "commercial-lease": "Commercial Lease", "cease-and-desist": "Cease and Desist", "vehicle-bill-of-sale": "Vehicle Bill of Sale", "legal-document": "Legal Document" }[req?.documentType] || "Document";

  const card = { background: "var(--sign-card-bg)", color: "var(--sign-card-fg)", borderRadius: 12, boxShadow: "0 10px 34px rgba(0,0,0,0.35)" };

  return (
    <IonApp className="sign-app">
      <IonContent className="sign-content">
        <div className={"sign-page" + (lightTheme ? " sign-light" : "")} style={{ ...vars, minHeight: "100%", padding: "28px 16px 40px", boxSizing: "border-box", position: "relative" }}>
      {/* the brand waves, the size of the screen, behind everything */}
      <div className="sign-waves" aria-hidden="true" />
      {req?.owner && (
        <RippleButton type="button" className="ms-login-back sign-back" onClick={goBack} aria-label="Go back">
          <IonIcon icon={chevronBackOutline} />
        </RippleButton>
      )}
      <div style={{ maxWidth: 720, margin: "0 auto", position: "relative", zIndex: 1 }}>
        {error ? (
          <div style={{ minHeight: "calc(100vh - 68px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <IonCard style={{ ...card, margin: 0, width: "100%", maxWidth: 480, padding: 28, textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: "1rem" }}>{error}</p>
            </IonCard>
          </div>
        ) : !req && shell ? (
          <div style={{ minHeight: "calc(100vh - 68px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "relative", width: "100%", maxWidth: 460, paddingTop: 50 }}>
              <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                <div style={{ width: 100, height: 100, borderRadius: "50%", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", boxShadow: "0 6px 20px rgba(0,0,0,0.28)" }}>
                  {shell.branding?.logo
                    ? <img src={shell.branding.logo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8, boxSizing: "border-box" }} />
                    : <img src={uiAvatar(shell.branding?.name || "Business")} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
              </div>
              <IonCard className="sign-verify-card" style={{ ...card, margin: 0, padding: "62px 26px 26px", textAlign: "center" }}>
                {shell.status === "signed" ? (
                  <>
                    {/* signed: the signer confirms their email to see where it stands, and to get their copy once approved */}
                    {shell.approval === "pending"
                      ? <IonIcon icon={timeOutline} style={{ fontSize: 46, color: "var(--ion-color-warning, #ffc409)" }} />
                      : <SignedCheck />}
                    <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>{shell.approval === "pending" ? "Signed, waiting for approval" : "All signed"}</p>
                    <p style={{ margin: "0 0 16px", fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                      {shell.approval === "pending"
                        ? `${shell.branding?.name || "The sender"} is reviewing your signature. Confirm your email to check on it.`
                        : "Confirm your email to get your signed copy."}
                    </p>
                    <IonInput fill="outline" labelPlacement="floating" label="Your email" type="email" inputmode="email"
                      value={verifyEmail} onIonInput={(e) => setVerifyEmail(e.detail.value ?? "")}
                      onKeyDown={(e) => { if (e.key === "Enter" && !verifying) verify(); }}
                      style={{ marginBottom: 12, "--color": "var(--sign-card-fg)", textAlign: "left" }} />
                    <IonButton expand="block" color="primary" disabled={verifying} onClick={verify} style={{ "--color": "#ffffff" }}>
                      {verifying ? <IonSpinner name="crescent" /> : "Open"}
                    </IonButton>
                  </>
                ) : shell.status === "expired" || shell.status === "canceled" ? (
                  <>
                    <IonIcon icon={timeOutline} style={{ fontSize: 42, color: "var(--sign-faint)" }} />
                    <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>{shell.status === "expired" ? "This request expired" : "This request was canceled"}</p>
                    <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                      Ask {shell.branding?.name || "the sender"} to send a new one.
                    </p>
                  </>
                ) : shell.status === "declined" ? (
                  <>
                    {/* declined: closed the same way, under the red X */}
                    <SignedCheck declined />
                    <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>Declined</p>
                    <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                      This document was declined and is closed. {shell.branding?.name || "The sender"} has been told.
                    </p>
                  </>
                ) : (
                  <>
                    <h1 style={{ margin: "0 0 10px", fontSize: "1.35rem", fontWeight: 700 }}>A document is waiting for you</h1>
                    <p style={{ margin: "0 0 16px", fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                      It was sent to <strong>{shell.recipientEmailMasked}</strong>. Confirm that address to open it.
                    </p>
                    <IonInput fill="outline" labelPlacement="floating" label="Your email" type="email" inputmode="email"
                      value={verifyEmail} onIonInput={(e) => setVerifyEmail(e.detail.value ?? "")}
                      onKeyDown={(e) => { if (e.key === "Enter" && !verifying) verify(); }}
                      style={{ marginBottom: 12, "--color": "var(--sign-card-fg)", textAlign: "left" }} />
                    <IonButton expand="block" color="primary" disabled={verifying} onClick={verify} style={{ "--color": "#ffffff" }}>
                      {verifying ? <IonSpinner name="crescent" /> : "Open the document"}
                    </IonButton>
                  </>
                )}
              </IonCard>
            </div>
          </div>
        ) : !req ? (
          <div style={{ minHeight: "calc(100vh - 68px)", display: "flex", alignItems: "center", justifyContent: "center" }}><IonSpinner name="crescent" style={{ color: "#fff", textDecoration: "underline" }} /></div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", margin: "44px 0 6px", position: "relative", zIndex: 3 }}>
              <IonBadge color={badgeColor} className="sign-status-badge">{badgeText}</IonBadge>
              <div className="sign-top-controls">
                <LanguagePicker value={language} onChange={setLanguage} />
                <IonButton fill="clear" className="sign-theme-toggle" aria-label={lightTheme ? "Dark mode" : "Light mode"} onClick={toggleTheme}>
                  <IonIcon slot="icon-only" icon={lightTheme ? moonOutline : sunnyOutline} />
                </IonButton>
              </div>
            </div>

            {/* the business card, its logo overlapping the top edge */}
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", top: -50, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                <div style={{ width: 100, height: 100, borderRadius: "50%", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", boxShadow: "0 6px 20px rgba(0,0,0,0.28)" }}>
                  {req.branding?.logo
                    ? <img src={req.branding.logo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8, boxSizing: "border-box" }} />
                    : <img src={uiAvatar(req.branding?.name || "Business")} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
              </div>
              <IonCard className="sign-card" style={{ ...card, margin: 0, paddingTop: 58, textAlign: "center" }}>
                <p style={{ margin: "0 0 4px", fontSize: "0.9rem", color: "var(--sign-muted)" }}>{req.branding?.name}</p>
                <h1 style={{ margin: "0 0 10px", fontSize: "1.5rem", fontWeight: 700 }}>Signature Request</h1>
                <p style={{ margin: "0 0 6px", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.9rem", color: "var(--sign-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  <IonIcon icon={personOutline} /> {req.recipientName || req.recipientEmail}
                </p>
                <p style={{ margin: "0 0 6px", fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                  {req.owner
                    ? `You asked ${req.recipientName || req.recipientEmail} to review and sign ${isCustom ? `“${label}”` : `this ${label.toLowerCase()}`}.` // the sender's own view
                    : `${req.branding?.name} is asking you to review and sign ${isCustom ? `“${label}”` : `this ${label.toLowerCase()}`}.`}
                  {isCustom && (req.signerCount || 0) > 1 ? ` Signer ${(req.signerIndex || 0) + 1} of ${req.signerCount}.` : ""}
                </p>
                {req.note && <p style={{ margin: "0 0 6px", fontSize: "0.9rem", fontStyle: "italic", color: "var(--sign-strong)" }}>&ldquo;{req.note}&rdquo;</p>}
                <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "var(--sign-faint)" }}>
                  {req.createdAt ? new Date(req.createdAt).toLocaleString("en-US", { month: "numeric", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit" }) : ""}
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <IonButton color={lightTheme ? "light" : "dark"} onClick={downloadPdf} disabled={downloadLocked}>
                    <IonIcon slot="start" icon={downloadOutline} /> Download PDF
                  </IonButton>
                  <IonButton color={lightTheme ? "light" : "dark"} className="sign-comments-btn" disabled={(req.status === "signed" && !awaitingApproval(req)) || ["declined", "expired", "canceled"].includes(req.status)} onClick={openComments}>
                    <IonIcon slot="start" icon={chatbubbleOutline} /> Comments
                    <IonBadge color="danger" style={{ marginLeft: 8 }}>{comments.length}</IonBadge>
                  </IonButton>
                </div>
              </IonCard>
            </div>

            {/* the document */}
            <IonCard className="sign-doc-card" style={{ ...card, margin: "16px 0 0", padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>{label}</span>
              </div>
              {isCustom && !finalBlob ? (
                docPages
                  ? <SignDocCanvas pages={docPages} fields={req.docFields || []} roles={req.docRoles || []}
                      paged page={pageIdx} onPageChange={setPageIdx}
                      mode={req.status === "pending" && !req.owner ? "fill" : "view"} activeRole={req.roleId}
                      values={{ ...(req.fieldValues || {}), ...myValues }}
                      onValue={(fid, v) => setMyValues((m) => ({ ...m, [fid]: v }))}
                      onSignField={(f) => { setPadSig(null); setPadFor(f); }} />
                  : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 260, color: "var(--sign-faint)" }}><IonSpinner name="crescent" /><span style={{ marginTop: 8, fontSize: "0.8rem" }}>Preparing the document…</span></div>
              ) : pages.length > 0
                ? <PreviewPager pages={pages} index={Math.min(pageIdx, pages.length - 1)} onIndexChange={setPageIdx} altPrefix={`${label} page`} />
                : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 260, color: "var(--sign-faint)" }}><IonSpinner name="crescent" /><span style={{ marginTop: 8, fontSize: "0.8rem" }}>Preparing the document…</span></div>}
            </IonCard>

            {/* sign / outcome */}
            {req.status === "pending" && req.owner && (
              <IonCard className="sign-waiting-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: "1.05rem" }}>Waiting for {req.recipientName || req.recipientEmail}</p>
                <p style={{ margin: "6px 0 0", fontSize: "0.9rem", color: "var(--sign-muted)" }}>You sent this document, so there is nothing for you to sign here.</p>
              </IonCard>
            )}
            {req.status === "pending" && !req.owner && (
              <IonCard className="sign-pad-card" style={{ ...card, margin: "16px 0 0", padding: 16 }}>
                {req.rejectCount > 0 && (
                  <div className="sign-resign-note" style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", marginBottom: 14, borderRadius: 8, background: "var(--ion-color-warning, #ffc409)", color: "#ffffff" }}>
                    <IonIcon icon={refreshOutline} style={{ fontSize: 20, flexShrink: 0, marginTop: 1, color: "#ffffff" }} />
                    <div style={{ fontSize: "0.9rem", textAlign: "left" }}>
                      <strong>{req.branding?.name} asked you to sign again.</strong>
                      {req.rejectReason ? <div style={{ marginTop: 4, color: "#ffffff", fontStyle: "italic" }}>&ldquo;{req.rejectReason}&rdquo;</div> : null}
                    </div>
                  </div>
                )}
                <span style={{ fontWeight: 700, fontSize: "1.05rem", display: "block", marginBottom: 10 }}>
                  {isCustom ? `Signing as ${req.fieldLabel || "signer"}` : (req.fieldLabel || "Your signature")}
                </span>
                <IonInput fill="outline" labelPlacement="floating" label="Your name" value={signerName}
                  onIonInput={(e) => setSignerName(e.detail.value ?? "")} style={{ marginBottom: 10, "--color": "var(--sign-card-fg)" }} />
                {isCustom ? (() => {
                  const missing = missingRequired(req.docFields, req.roleId, myFieldValues());
                  const left = missing.length;
                  // the next empty field sits on another page: say which
                  const next = left ? nextMissing(missing) : null;
                  const onPage = next && (docPages?.length || 0) > 1 && typeof next.page === "number" && next.page !== pageIdx ? next.page + 1 : 0;
                  return (
                    <p style={{ margin: "4px 0 0", fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                      {left ? `Fill in your highlighted fields on the document above: ${left} still to go${onPage ? `, the next on page ${onPage}` : ""}.` : "Everything is filled in. Sign to finish."}
                    </p>
                  );
                })() : <SignaturePad height={150} onChange={setSignature} dark={!lightTheme} />}
                <IonButton expand="block" color="success" disabled={busy} onClick={sign} style={{ marginTop: 14, "--color": "#ffffff" }}>
                  {busy ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={checkmarkOutline} /> Sign &amp; Approve</>}
                </IonButton>
              </IonCard>
            )}
            {req.status === "pending" && !req.owner && (
              <div className="sign-decline-area" style={{ textAlign: "center", marginTop: 22 }}>
                <p style={{ margin: "0 0 10px", fontSize: "0.9rem", color: "#ffffff", opacity: 0.92 }}>
                  If you choose not to sign, please click Decline below.
                </p>
                <IonButton color="danger" disabled={busy} onClick={decline}>
                  <IonIcon slot="start" icon={closeOutline} /> DECLINE DOCUMENT
                </IonButton>
              </div>
            )}
            {req.status === "signed" && (req.forwardedTo || req.isFinal === false) && (
              <IonCard className="sign-forwarded-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <SignedCheck animate={justSigned} />
                <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>Signed, one more to go</p>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                  {req.owner && req.forwardedTo
                    ? `${req.signerName || req.recipientName || req.recipientEmail} signed. The document went on to ${req.forwardedTo} for theirs.` // the sender may see who
                    : "Your signature is in. The document went on to the next signer."}
                </p>
              </IonCard>
            )}
            {awaitingApproval(req) && !req.owner && (
              <IonCard className="sign-done-card sign-awaiting-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <SignedCheck animate={justSigned} />
                <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>Signed, waiting for approval</p>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                  {req.branding?.name} is reviewing your signature. You'll get an email once it's approved, and your signed copy will be ready here.
                </p>
              </IonCard>
            )}
            {awaitingApproval(req) && req.owner && (
              <IonCard className="sign-done-card sign-approve-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>Review the signature</p>
                <p style={{ margin: "0 0 14px", fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                  {req.signerName || req.recipientName || req.recipientEmail} signed this document. Approve it to make the final signed copy, or send it back to be signed again.
                </p>
                {!req.trusted && (
                  /* a plain row: only the checkbox itself toggles, not the text */
                  <div className="sign-trust-row" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, maxWidth: 420, margin: "0 auto 14px", color: "var(--sign-card-fg)" }}>
                    <IonCheckbox checked={trustSigner} onIonChange={(e) => setTrustSigner(e.detail.checked)} aria-label={`Always approve signatures from ${req.recipientEmail}`} />
                    <span style={{ fontSize: "0.88rem", textAlign: "left", userSelect: "text" }}>
                      Always approve signatures from {req.recipientEmail}
                    </span>
                  </div>
                )}
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <IonButton color="success" disabled={busy} onClick={approve} style={{ "--color": "#ffffff" }}>
                    {busy ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={checkmarkOutline} /> Approve</>}
                  </IonButton>
                  <IonButton color="danger" disabled={busy || (req.rejectCount || 0) >= (req.rejectMax || 3)} onClick={sendBack}>
                    <IonIcon slot="start" icon={refreshOutline} /> Send back to re-sign
                  </IonButton>
                </div>
                <p style={{ margin: "10px 0 0", fontSize: "0.78rem", color: "var(--sign-faint)" }}>
                  {(req.rejectCount || 0) >= (req.rejectMax || 3)
                    ? "This has been sent back as many times as allowed. Approve it, or cancel it from Signatures and send a new request."
                    : `Sent back ${req.rejectCount || 0} of ${req.rejectMax || 3} times. Unapproved signatures expire after 30 days.`}
                </p>
              </IonCard>
            )}
            {(req.status === "expired" || req.status === "canceled") && (
              <IonCard className="sign-done-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <IonIcon icon={timeOutline} style={{ fontSize: 42, color: "var(--sign-faint)" }} />
                <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>{req.status === "expired" ? "This request expired" : "This request was canceled"}</p>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                  {req.status === "expired" ? "The signature was never approved, so no signed copy was made." : "No signed copy was made."}
                </p>
              </IonCard>
            )}
            {req.status === "signed" && !awaitingApproval(req) && !req.forwardedTo && req.isFinal !== false && (
              <IonCard className="sign-done-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <SignedCheck animate={justSigned} />
                <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>All signed</p>
                <p style={{ margin: "0 0 14px", fontSize: "0.9rem", color: "var(--sign-muted)" }}>
                  {req.owner
                    ? `${req.signerName || req.recipientName || req.recipientEmail} signed this document. You're done.` // the sender's own view
                    : `${req.branding?.name} approved your signature. You're done.`}
                </p>
                {finalBlob && (
                  <IonButton fill="outline" onClick={downloadCopy} style={{ "--color": "var(--sign-card-fg)", "--border-color": "var(--sign-faint)" }}>
                    <IonIcon slot="start" icon={downloadOutline} /> Download a copy
                  </IonButton>
                )}
              </IonCard>
            )}
            {req.status === "declined" && (
              <IonCard className="sign-done-card" style={{ ...card, margin: "16px 0 0", padding: 24, textAlign: "center" }}>
                <SignedCheck declined animate={justSigned} />
                <p style={{ margin: "10px 0 4px", fontWeight: 700, fontSize: "1.1rem" }}>Declined</p>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--sign-muted)" }}>The sender has been told.</p>
              </IonCard>
            )}

            {/* footer */}
            <div style={{ textAlign: "center", marginTop: 26, color: "#ffffff" }}>
              {(req.branding?.email || req.branding?.phone) && (
                <p style={{ margin: "0 0 10px", fontSize: "0.85rem", opacity: 0.9 }}>
                  Questions? Contact {req.branding?.name}
                  {req.branding?.email ? <> at <a href={`mailto:${req.branding.email}`} style={{ color: "#fff", textDecoration: "underline" }}>{req.branding.email}</a></> : null}
                  {req.branding?.phone ? <> or call <a href={`tel:${req.branding.phone}`} style={{ color: "#fff", textDecoration: "underline" }}>{fmtPhone(req.branding.phone)}</a></> : null}.
                </p>
              )}
              <p style={{ margin: 0, fontSize: "0.8rem", opacity: 0.9, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                Powered by
                <img src={MintSlipLogo} alt="MintSlip" style={{ height: 18, display: "block", filter: "brightness(0) invert(1)" }} />
              </p>
            </div>
          </>
        )}
      </div>
        </div>
        {padFor && (
          <EditSheet title={padFor.type === "initials" ? "Draw your initials" : "Draw your signature"} onClose={() => setPadFor(null)} saveLabel="Apply"
            onSave={() => {
              if (!padSig) { toast.error(padFor.type === "initials" ? "Draw your initials first" : "Draw your signature first"); return; }
              // the same mark fills each of this signer's empty fields of the kind
              setMyValues((m) => {
                const next = { ...m, [padFor.id]: padSig };
                for (const f of req.docFields || []) if (f.role === req.roleId && f.type === padFor.type && !next[f.id]) next[f.id] = padSig;
                return next;
              });
              setPadFor(null);
            }}>
            <SignaturePad height={170} onChange={setPadSig} dark={!lightTheme} />
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--ion-color-medium)" }}>
              It goes in every {padFor.type === "initials" ? "initials" : "signature"} box that is yours. Tap a box again to redraw it.
            </p>
          </EditSheet>
        )}
        <CommentsThreadModal dark={!lightTheme} isOpen={commentsOpen} onClose={() => setCommentsOpen(false)}
          comments={comments} value={commentText} onValue={setCommentText}
          busy={commentBusy} onSubmit={addComment} />
      </IonContent>
    </IonApp>
  );
}
